// Analyze Landing

import {
  ActivityIndicator,
  Platform,
  Pressable,
  TouchableOpacity,
  View,
} from "react-native"
import { router, useFocusEffect } from "expo-router"
import SpyGlass from "@/assets/icons/spyglass.svg"
import { useViewedCallback } from "@/analytics/react"

import * as AnalyzerEvents from "@/analytics/events/analyzer"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { Body, H2, H4, H5, Small, Title } from "@/components/standard/BFText"
import { BFTextInput } from "@/components/standard/BFTextInput"
import MarketDataLegalText, {
  emptyPlaceholder,
} from "@/components/analysis/MarketData"
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated"
import MarketDataTitleTooltipRow from "@/components/analysis/MarketDataTitleTooltipRow"
import { useUser } from "@/queries/user"
import { useMarketData } from "@/queries/markettrends"
import StockUp from "@/assets/icons/stock-green-up.svg"
import StockDown from "@/assets/icons/stock-red-down.svg"
import React, { useCallback, useEffect, useState } from "react"
import { type MarketDataType } from "@/generated/graphql"
import {
  abbreviate,
  abbreviateCurrency,
  formatShortDate,
  numberToPercentage,
  roundToDecimal,
} from "@/utils/NumberUtils"
import { marketTrendsButtonClicked } from "@/analytics/events/marketdata"
import { useIsMutating } from "@tanstack/react-query"
import * as Location from "expo-location"
import { BFColors } from "@/constants/Colors"
import Pluralize from "@/utils/Pluralize"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { BFScreen } from "@/components/standard/screen/BFScreen"
import { useSafeAreaInsets } from "react-native-safe-area-context"

export default function Analyze() {
  const insets = useSafeAreaInsets()

  useViewedCallback(() => {
    setStatusBarStyle("light")
    AnalyzerEvents.analyzeViewed()
  })

  const isDesktop = useIsDesktop()

  return (
    <BFScreen
      noBackButton={true}
      headerSticky={false}
      dark={true}
      gradientBackground={true}
      includeStandardSideMargins={true}
      ignoreTopSafeArea={true}
      header={
        <Pressable
          onPress={() => {
            AnalyzerEvents.analyzePropertySearchClicked()
            router.push("/analyze/search")
          }}
          testID={"analyze-header-click"}
          className="flex flex-col justify-end pb-2"
          style={{
            height: Platform.OS === "web" ? 80 : insets.top + 60,
          }}
        >
          {!isDesktop && <H2 className="text-white">Analyze</H2>}
        </Pressable>
      }
    >
      <View className="flex flex-col flex-1 bg-transparent items-center">
        <View className={isDesktop ? `w-[650px]` : "size-full"}>
          {isDesktop && (
            <H2 className="text-white mb-4 items-start">Analyze</H2>
          )}
          <View className="flex">
            <Pressable
              testID={"spyglass-search-button"}
              onPressIn={() => {
                AnalyzerEvents.analyzePropertySearchClicked()
                // android
                router.push("/analyze/search")
              }}
            >
              <BFTextInput
                testID="analyze-search-pressable"
                placeholder={"Property Address"}
                readOnly={true}
                onPressIn={() => {
                  AnalyzerEvents.analyzePropertySearchClicked()
                  // ios
                  router.push("/analyze/search")
                }}
                className="w-full h-[50px] py-[14px] pl-[6px] rounded bg-white font-spacegrotesk border-transparent placeholder:text-warm-grey placeholder:font-spacegrotesk border"
              />
            </Pressable>
            <View className="absolute top-[16px] right-[20px]">
              <SpyGlass width={17} height={17} />
            </View>
          </View>
          <View className="flex flex-col items-center justify-center flex-1">
            <View className="flex items-center justify-start flex-1 mt-[40px]">
              <Title className="text-teal text-[24px] font-figtree-extrabold text-center w-[290px] web:mb-20">
                Get MLS Comps and Evaluate Profit Instantly
              </Title>
            </View>

            <MarketDataBlock />
            <View className="h-20" />
          </View>
        </View>
      </View>
    </BFScreen>
  )
}

interface UserLocationCoords {
  latitude: number
  longitude: number
}

const MarketDataBlock = () => {
  const { data: user, isLoading: userIsLoading } = useUser()
  const [userLocation, setUserLocation] = useState<UserLocationCoords | null>(
    null
  )

  useEffect(() => {
    void (async () => {
      const { status } = await Location.requestForegroundPermissionsAsync()
      if (status !== "granted") {
        console.log("Permission to access location was denied")
        return
      }

      try {
        const location = await Location.getCurrentPositionAsync({})
        setUserLocation({
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        })
      } catch (err) {
        console.warn(err)
      }
    })()
  }, [])

  const {
    data: marketData,
    isLoading: marketDataIsLoading,
    isPending: marketDataIsPending,
  } = useMarketData(userLocation?.latitude, userLocation?.longitude)

  // Catches any mutation and works well for this purpose
  const isMutating = useIsMutating()

  if (marketDataIsLoading || marketDataIsPending || isMutating || !marketData) {
    // TODO: Perhaps render a different component/message when marketData is not available at all.
    // to indicate error rather than a perpertual loading state. Using the toast may be excessive here.
    return <ActivityIndicator size={"small"} />
  }

  let lastUpdated: string = emptyPlaceholder
  let periodBegin: string = emptyPlaceholder
  let periodEnd: string = emptyPlaceholder

  if (marketData.lastUpdated) {
    lastUpdated = formatShortDate(marketData.lastUpdated) || emptyPlaceholder
  }

  if (marketData.periodBegin) {
    periodBegin = formatShortDate(marketData.periodBegin) || emptyPlaceholder
  }

  if (marketData.periodEnd) {
    periodEnd = formatShortDate(marketData.periodEnd) || emptyPlaceholder
  }

  if (userIsLoading || !user?.me) {
    return null
  }

  return (
    <View className="flex-1 max-w-[375px] max-h-[200px] mb-12">
      <MarketTiles marketData={marketData as MarketDataType} />
      <MarketDataLegalText
        provider="Redfin"
        asOfDate={lastUpdated}
        startDate={periodBegin}
        endDate={periodEnd}
      />
    </View>
  )
}

interface MarketTilesProps {
  marketData: MarketDataType
}

const MarketTiles = ({ marketData }: MarketTilesProps) => {
  const handleLocationTap = () => {
    marketTrendsButtonClicked()
    router.push("/(tabs)/analyze/marketdata/regionsearch")
  }

  const pickedRegionText: string = (() => {
    if (marketData.city || marketData.state) {
      return `${marketData.city}, ${marketData.state}`
    }
    return marketData.zipcode?.toString() || "---"
  })()

  return (
    <View className="px-4 mb-1 web:w-[375px]">
      <View className="flex-row">
        <H4 className="text-lightest mb-2 pr-1">Market Trends for </H4>
        <TouchableOpacity
          testID={"location-area-action"}
          onPress={handleLocationTap}
        >
          <Body className="text-lightest underline">{pickedRegionText}</Body>
        </TouchableOpacity>
      </View>
      <View className="flex-row justify-between items-center mb-[10px]">
        <MarketTile marketData={marketData} delay={0} type="medianPrice" />
        <MarketTile marketData={marketData} delay={100} type="daysOnMarket" />
        <MarketTile marketData={marketData} delay={200} type="saleToList" />
      </View>
      <View>
        <MarketTile marketData={marketData} delay={300} type="inventory" />
      </View>
    </View>
  )
}

interface MarketTileProps {
  delay: number
  type: "medianPrice" | "daysOnMarket" | "saleToList" | "inventory"
  marketData: MarketDataType
}

const MarketTile = ({ delay, type, marketData }: MarketTileProps) => {
  const opacity = useSharedValue(0)
  let directionArrow: "up" | "down" | "none" = "up"
  const directionArrowFor = (value: number): "up" | "down" | "none" => {
    if (value === 0) return "none"
    return value < 0 ? "down" : "up"
  }

  const title: string = (() => {
    switch (type) {
      case "medianPrice":
        return "MEDIAN PRICE"
      case "daysOnMarket":
        return "MEDIAN DOM"
      case "saleToList":
        return "SALE TO LIST"
      case "inventory":
        return "INVENTORY"
      default:
        return "TBD"
    }
  })()

  const amount: string | string[] = (() => {
    switch (type) {
      case "medianPrice":
        return (
          abbreviateCurrency(marketData.medianSalePrice) || emptyPlaceholder
        )
      case "daysOnMarket":
        return marketData.medianDom?.toString() || emptyPlaceholder
      case "saleToList":
        return (
          numberToPercentage((marketData.avgSaleToList || 0) * 100, 0) ||
          emptyPlaceholder
        )
      case "inventory":
        return [
          abbreviate(marketData.inventory || 0).toString(),
          abbreviate(marketData.homesSold || 0).toString(),
          abbreviate(marketData.newListings || 0).toString(),
        ]
      default:
        return "N/A"
    }
  })()

  const suffix: string | string[] | null = (() => {
    switch (type) {
      case "daysOnMarket":
        return "days"
      case "inventory":
        return ["EOM", "sold", "new"]
      default:
        return null
    }
  })()

  const momValue: number | number[] | null = (() => {
    switch (type) {
      case "medianPrice":
        directionArrow = directionArrowFor(marketData.medianSalePriceMom || 0)
        return Math.abs(marketData.medianSalePriceMom || 0) * 100 || null
      case "daysOnMarket":
        directionArrow = directionArrowFor(marketData.medianDomMom || 0)
        return roundToDecimal(marketData.medianDomMom || 0, 1) || null
      case "saleToList":
        directionArrow = directionArrowFor(marketData.avgSaleToListMom || 0)
        return Math.abs(marketData.avgSaleToListMom || 0) * 100 || 0
      case "inventory":
        return [
          Math.abs(marketData.inventoryMom || 0) * 100 || 0,
          Math.abs(marketData.homesSoldMom || 0) * 100 || 0,
          Math.abs(marketData.newListingsMom || 0) * 100 || 0,
        ]
      default:
        return 0
    }
  })()

  useFocusEffect(
    useCallback(() => {
      opacity.value = 0
      opacity.value = withDelay(delay, withTiming(1, { duration: 500 }))

      // Reset opacity when the component loses focus
      return () => {
        opacity.value = 0
      }
    }, [])
  )

  const animatedStyles = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }))

  const handleCellTap = () => {
    router.push("/(tabs)/analyze/marketdata/qualitativefeedback")
  }

  if (type !== "inventory") {
    return (
      <TouchableOpacity
        testID={`tile-${type}`}
        onPress={handleCellTap}
        className="w-[33%] bg-midnight-blue rounded-[4px] p-3 h-[84px] max-w-[110px]"
      >
        <Animated.View style={animatedStyles}>
          <MarketDataTitleTooltipRow title={title} type={type} />

          <MarketTileReadout
            amount={amount}
            suffix={suffix}
            momValue={momValue}
            momValueSuffix={type !== "daysOnMarket" ? "%" : ""}
            directionArrow={directionArrow}
          />
        </Animated.View>
      </TouchableOpacity>
    )
  }

  // Build Inventory Cell
  const elements: JSX.Element[] = []

  for (let i = 0; i < 3; i++) {
    elements.push(
      <MarketTileReadout
        key={i}
        amount={amount[i]}
        suffix={suffix ? suffix[i] : null}
        momValue={Array.isArray(momValue) ? momValue[i] : momValue}
        directionArrow={directionArrowFor(
          Array.isArray(momValue) ? momValue[i] : momValue || 0
        )}
      />
    )
  }

  return (
    <TouchableOpacity
      testID={"feedback-touch-area"}
      onPress={handleCellTap}
      className="relative w-full bg-midnight-blue rounded-[4px] p-3 h-[84px]"
    >
      <View
        style={{
          position: "absolute",
          top: 32,
          left: Platform.OS === "web" ? "33%" : "35%",
          height: 21,
          width: 1,
          backgroundColor: BFColors.lightest,
        }}
      />

      <View
        style={{
          position: "absolute",
          top: 32,
          right: Platform.OS === "web" ? "33%" : "35%",
          height: 21,
          width: 1,
          backgroundColor: BFColors.lightest,
        }}
      />
      <Animated.View style={animatedStyles}>
        <MarketDataTitleTooltipRow title={title} type={type} />
        <View className="flex-row justify-between">{elements}</View>
      </Animated.View>
    </TouchableOpacity>
  )
}

interface MarketTileReadoutProps {
  amount: string | string[]
  suffix: string | string[] | null
  momValue: number | number[] | null
  momValueSuffix?: string
  directionArrow: "up" | "down" | "none"
  hasDividerLeft?: boolean
  hasDividerRight?: boolean
}

const MarketTileReadout: React.FC<MarketTileReadoutProps> = ({
  amount,
  suffix,
  momValue,
  momValueSuffix = "%",
  directionArrow,
}: MarketTileReadoutProps) => {
  let momValuePercent: string = emptyPlaceholder
  const isStandardMoMDisplay: boolean = momValueSuffix !== ""

  if (typeof momValue === "number") {
    momValuePercent =
      momValue !== null
        ? `${Math.abs(isStandardMoMDisplay ? Math.round(momValue) : momValue)}${momValueSuffix}`
        : emptyPlaceholder
  }

  return (
    <View className="flex-row min-w-[88px]">
      <View>
        <View className="flex-row">
          <H2 className="text-lightest mt-1">{amount}</H2>
          {suffix && (
            <H4 className="text-lightest ml-[4px] mt-[9px]">{suffix}</H4>
          )}
        </View>
        <View className="flex-row items-end">
          <H5 className="text-lightest">{momValuePercent}</H5>
          <Small
            className={`text-lightest ml-[2px] ${!isStandardMoMDisplay ? "text-[10px] pt-[2px]" : ""}`}
          >
            {isStandardMoMDisplay
              ? `MoM`
              : `${Pluralize.pluralize("Day", typeof momValue === "number" ? momValue : 0, "", false)} MoM`}
          </Small>

          <View className="ml-1 mb-[3px]">
            {directionArrow === "up" ? <StockUp /> : <StockDown />}
          </View>
        </View>
      </View>
    </View>
  )
}
